import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { budgetKeys } from '~/lib/query-keys';

/**
 * The platform's compute budget for the signed-in person (FRONT-1194).
 *
 * `enforced: false` is the normal case for a workspace with no cost control —
 * every surface must render nothing at all rather than a zeroed meter, because
 * the platform sends placeholder figures alongside that flag.
 *
 * One query backs three things: the sidebar meter, the settings card, and the
 * chat blocked state. The blocked state deliberately reads from here rather
 * than parsing a refusal out of the chat stream — the streaming transport
 * doesn't surface a structured error body, and the composer needs to know
 * before the person types, not after they hit send.
 */
export type BudgetTimeframe = 'monthly' | 'weekly' | 'daily';
export type BudgetSource = 'override' | 'group' | 'default' | 'system_default';

export interface Budget {
  enforced: true;
  used: number;
  limit: number;
  baseLimit: number;
  extensionCb: number;
  limitReached: boolean;
  timeframe: BudgetTimeframe;
  periodStartsAt: string | null;
  periodResetsAt: string | null;
  source: BudgetSource;
  fractionUsed: number;
  copy: {
    headline: string | null;
    message: string | null;
    banner: string | null;
  };
  topUp: {
    enabled: boolean;
    amounts: number[];
    codeGuidance: string | null;
  };
  warnings: {
    bannerEnabled: boolean;
    thresholds: { percent: number }[];
  };
}

export type BudgetResponse = { enforced: false } | Budget;

export function useBudget() {
  return useQuery<BudgetResponse>({
    queryKey: budgetKeys.me(),
    queryFn: () => api.get<BudgetResponse>('/api/limits/me'),
    // Matches the platform frontend's own cadence. The backend caches the
    // upstream read, so polling costs us nothing extra.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** The budget only when cost control is actually on — the common guard. */
export function useEnforcedBudget(): Budget | null {
  const { data } = useBudget();
  return data?.enforced ? data : null;
}

export function useRequestTopUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { amount: number; projectCode: string }) =>
      api.post<{ ok: true }>('/api/limits/me/top-up', vars),
    onSuccess: () => {
      // A granted top-up raises the ceiling — the meter and any blocked state
      // must reflect it immediately.
      void qc.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

// ---- Admin (Surface A) ----

export interface OrgBudgetRow {
  userId: string;
  /** Resolved from the platform's org directory (FRONT-1374); null when the
   * lookup failed or the id is unknown there — the row then shows the id. */
  displayName: string | null;
  email: string | null;
  limit: number;
  timeframe: BudgetTimeframe;
  used: number | null;
  extensionCb: number | null;
  source: BudgetSource;
  limitReached: boolean;
}

/**
 * `readable: false` means the caller lacks the platform's cost-control
 * permission. Eddy admin and platform admin are separate role systems, so this
 * is an expected state, not an error — the panel says so plainly instead of
 * showing an empty table that would read as "nobody is over budget".
 */
export type OrgBudgetResponse =
  | { readable: false }
  | {
      readable: true;
      enforced: boolean;
      defaultLimit: number;
      timeframe: BudgetTimeframe;
      topUp: { enabled: boolean; amounts: number[]; codeGuidance: string | null };
      warnings: { bannerEnabled: boolean; thresholds: { percent: number }[] };
      stopped: OrgBudgetRow[];
      stoppedTotal: number;
      stoppedReadable: boolean;
    };

export function useOrgBudget() {
  return useQuery<OrgBudgetResponse>({
    queryKey: budgetKeys.org(),
    queryFn: () => api.get<OrgBudgetResponse>('/api/admin/limits/org'),
    staleTime: 60_000,
  });
}
